import { lazy } from "react";

// Registry of "embed" widgets -> existing functional page bodies.
// Lazy dynamic imports avoid circular-dependency init issues (page files import PageRenderer).
export const WIDGETS = {
  home_hero: lazy(() => import("@/components/common/Hero").then((m) => ({ default: m.Hero }))),
  home_body: lazy(() => import("@/pages/Home").then((m) => ({ default: m.HomeBody }))),
  about_body: lazy(() => import("@/pages/About").then((m) => ({ default: m.AboutBody }))),
  about_coaches: lazy(() => import("@/pages/About").then((m) => ({ default: m.AboutCoaches }))),
  locations_body: lazy(() => import("@/pages/Locations").then((m) => ({ default: m.LocationsBody }))),
  services_body: lazy(() => import("@/pages/Services").then((m) => ({ default: m.ServicesBody }))),
  packages_body: lazy(() => import("@/pages/Packages").then((m) => ({ default: m.PackagesBody }))),
  booking_body: lazy(() => import("@/pages/Booking").then((m) => ({ default: m.BookingBody }))),
  mavs_body: lazy(() => import("@/pages/MavsVolleyball").then((m) => ({ default: m.MavsBody }))),
  offers_body: lazy(() => import("@/pages/Offers").then((m) => ({ default: m.OffersBody }))),
  events_body: lazy(() => import("@/pages/Events").then((m) => ({ default: m.EventsBody }))),
  testimonials_body: lazy(() => import("@/pages/Testimonials").then((m) => ({ default: m.TestimonialsBody }))),
  faq_body: lazy(() => import("@/pages/FAQ").then((m) => ({ default: m.FAQBody }))),
  contact_body: lazy(() => import("@/pages/Contact").then((m) => ({ default: m.ContactBody }))),
  support_body: lazy(() => import("@/pages/Support").then((m) => ({ default: m.SupportBody }))),
  home_offer: lazy(() => import("@/components/widgets/HomeWidgets").then((m) => ({ default: m.HomeOffer }))),
  home_testimonials: lazy(() => import("@/components/widgets/HomeWidgets").then((m) => ({ default: m.HomeTestimonials }))),
  home_app: lazy(() => import("@/components/widgets/HomeWidgets").then((m) => ({ default: m.HomeApp }))),
  home_quick_links: lazy(() => import("@/components/widgets/HomeWidgets").then((m) => ({ default: m.HomeQuickLinks }))),
  home_locations: lazy(() => import("@/components/widgets/HomeWidgets").then((m) => ({ default: m.HomeLocations }))),
  home_social: lazy(() => import("@/components/widgets/HomeWidgets").then((m) => ({ default: m.HomeSocial }))),
  services_grid: lazy(() => import("@/components/widgets/ServicesWidgets").then((m) => ({ default: m.ServicesGrid }))),
  locations_cards: lazy(() => import("@/components/widgets/LocationsWidgets").then((m) => ({ default: m.LocationsCards }))),
  locations_details: lazy(() => import("@/components/widgets/LocationsWidgets").then((m) => ({ default: m.LocationsDetails }))),
  locations_directions: lazy(() => import("@/components/common/LocationsDirections").then((m) => ({ default: m.LocationsDirections }))),
  packages_grid: lazy(() => import("@/components/widgets/PageGridWidgets").then((m) => ({ default: m.PackagesGrid }))),
  events_grid: lazy(() => import("@/components/widgets/PageGridWidgets").then((m) => ({ default: m.EventsGrid }))),
  testimonials_grid: lazy(() => import("@/components/widgets/PageGridWidgets").then((m) => ({ default: m.TestimonialsGrid }))),
  faq_list: lazy(() => import("@/components/widgets/PageGridWidgets").then((m) => ({ default: m.FaqList }))),
  mavs_core: lazy(() => import("@/components/widgets/MavsWidgets").then((m) => ({ default: m.MavsBodyCore }))),
  mavs_steps: lazy(() => import("@/components/widgets/MavsWidgets").then((m) => ({ default: m.MavsSteps }))),
  mavs_app: lazy(() => import("@/components/widgets/MavsWidgets").then((m) => ({ default: m.MavsApp }))),
  mavs_schedule: lazy(() => import("@/components/widgets/MavsWidgets").then((m) => ({ default: m.MavsSchedule }))),
  mavs_packages: lazy(() => import("@/components/widgets/MavsWidgets").then((m) => ({ default: m.MavsPackages }))),
  mavs_highlights: lazy(() => import("@/components/widgets/MavsWidgets").then((m) => ({ default: m.MavsHighlights }))),
  mavs_parents: lazy(() => import("@/components/widgets/MavsWidgets").then((m) => ({ default: m.MavsParents }))),
  booking_start: lazy(() => import("@/components/widgets/BookingWidgets").then((m) => ({ default: m.BookingStart }))),
  booking_options: lazy(() => import("@/components/widgets/BookingWidgets").then((m) => ({ default: m.BookingOptions }))),
  booking_scheduler: lazy(() => import("@/components/widgets/BookingWidgets").then((m) => ({ default: m.BookingScheduler }))),
  offerings_grid: lazy(() => import("@/components/widgets/SharedWidgets").then((m) => ({ default: m.OfferingsGrid }))),
};

export const WIDGET_LABELS = {
  home_hero: "Home Hero", home_body: "Home Body", about_body: "About Body", about_coaches: "About Coaches",
  locations_body: "Locations Body", services_body: "Services Body", packages_body: "Packages Body",
  booking_body: "Booking Body", mavs_body: "Mavs Body", offers_body: "Offers Body",
  events_body: "Events Body", testimonials_body: "Testimonials Body", faq_body: "FAQ Body",
  contact_body: "Contact Body", support_body: "Support Body",
  home_offer: "Home · Current Offer", home_testimonials: "Home · Featured Testimonials",
  home_app: "Home · Trilogy App", home_quick_links: "Home · Quick Links",
  home_locations: "Home · Locations Preview", home_social: "Home · Social Strip",
  services_grid: "Services · Programs Grid",
  locations_cards: "Locations · Cards", locations_details: "Locations · Details", locations_directions: "Locations · Directions Map",
  packages_grid: "Packages · Grid", events_grid: "Events · Grid", testimonials_grid: "Testimonials · Grid", faq_list: "FAQ · List",
  mavs_core: "Mavs · Program Body",
  mavs_steps: "Mavs · Get Started Steps", mavs_app: "Mavs · Trilogy App Band",
  mavs_schedule: "Mavs · Schedule", mavs_packages: "Mavs · Packages & Camps",
  mavs_highlights: "Mavs · Athlete Highlights", mavs_parents: "Mavs · Announcements & Resources",
  booking_start: "Booking · Get Started", booking_options: "Booking · Options",
  booking_scheduler: "Booking · Live Scheduler",
  offerings_grid: "Shared · Find Your Path (from Home)",
};

// Maps a functional (embed) widget to the admin screen where its content is
// actually managed. Shown in the Page Editor so admins can jump straight to the
// right manager for non-on-page-editable sections. `null` = no manager (static).
export const WIDGET_MANAGERS = {
  // Home widgets
  home_offer: { to: "/admin/offers", label: "Edit Offers" },
  home_testimonials: { to: "/admin/testimonials", label: "Edit Testimonials" },
  home_locations: { to: "/admin/locations", label: "Edit Locations" },
  home_app: { to: "/admin/settings", label: "Edit App Settings" },
  home_social: { to: "/admin/settings", label: "Edit Social Links" },
  home_quick_links: null,
  // Coaches embed (About)
  about_coaches: { to: "/admin/coaches", label: "Edit Coaches" },
  services_grid: { to: "/admin/services", label: "Edit Services" },
  locations_cards: { to: "/admin/locations", label: "Edit Locations" },
  locations_details: { to: "/admin/locations", label: "Edit Locations" },
  locations_directions: { to: "/admin/locations", label: "Edit Locations" },
  packages_grid: { to: "/admin/packages", label: "Edit Packages" },
  events_grid: { to: "/admin/events", label: "Edit Events" },
  testimonials_grid: { to: "/admin/testimonials", label: "Edit Testimonials" },
  faq_list: { to: "/admin/faqs", label: "Edit FAQs" },
  mavs_core: { to: "/admin/volleyball_resources", label: "Edit Volleyball Resources" },
  mavs_steps: { to: "/admin/settings", label: "Edit Mavs Steps" },
  mavs_app: { to: "/admin/settings", label: "Edit App Settings" },
  mavs_schedule: { to: "/admin/events", label: "Edit Events" },
  mavs_packages: { to: "/admin/packages", label: "Edit Packages" },
  mavs_highlights: null,
  mavs_parents: { to: "/admin/volleyball_resources", label: "Edit Resources / Announcements" },
  booking_start: { to: "/admin/booking_links", label: "Edit Booking Links" },
  booking_options: { to: "/admin/booking_links", label: "Edit Booking Links" },
  booking_scheduler: { to: "/admin/booking_links", label: "Edit Booking Links" },
  offerings_grid: { to: "/admin/pages/home", label: "Edit 'Find Your Path' (Home)" },
  // Legacy full-page body embeds → their content managers
  offers_body: { to: "/admin/offers", label: "Edit Offers" },
  testimonials_body: { to: "/admin/testimonials", label: "Edit Testimonials" },
  locations_body: { to: "/admin/locations", label: "Edit Locations" },
  services_body: { to: "/admin/services", label: "Edit Services" },
  packages_body: { to: "/admin/packages", label: "Edit Packages" },
  events_body: { to: "/admin/events", label: "Edit Events" },
  faq_body: { to: "/admin/faqs", label: "Edit FAQs" },
  booking_body: { to: "/admin/booking_links", label: "Edit Booking Links" },
  mavs_body: { to: "/admin/volleyball_resources", label: "Edit Volleyball Resources" },
  contact_body: { to: "/admin/leads", label: "View Contact Leads" },
  support_body: { to: "/admin/tickets", label: "View Support Tickets" },
};
